<!--@format-->
<template>
    <div class="gst-hotel-reservation-info d-flex flex-column">
        <div
            v-if="hotelImageSrc"
            class="image-container">
            <img
                alt=""
                :src="hotelImageSrc" />
        </div>
        <div class="info-container">
            <div class="event-title-container">
                <span
                    v-if="hotelName"
                    class="event-title"
                    >{{ hotelName }}</span
                >
                <StarRating
                    v-if="hotelRating"
                    :value="hotelRating.rating"
                    class="event-rating" />
            </div>
            <div
                v-if="hotelSupplierConfirmationNumber"
                class="info-container__confirmation-number mb-4">
                {{ hotelConfirmationNumber }}
            </div>
            <div
                v-if="showGiftCard"
                class="mb-4">
                <GiftCardChip
                    :amount="giftCard.amount"
                    :currency="giftCard.currency" />
                <BaseTooltip
                    :text="$t('giftCardTooltip')"
                    :open-on-click="!$vuetify.breakpoint.mdAndUp"
                    :open-on-hover="$vuetify.breakpoint.mdAndUp"
                    class-content="gst-order-confirmation__hotel-info__tooltip"
                    class-activator="gst-order-confirmation__hotel-info__tooltip-activator ml-4 ml-md-2">
                    <BaseIcon
                        symbol-id="icons--help"
                        :width="18"
                        :height="18" />
                </BaseTooltip>
            </div>
            <div class="mb-2">
                <div class="info-container__wrapper-icon d-flex justify-center">
                    <CalendarIcon />
                </div>
                <span class="ml-2">
                    {{ period }}
                </span>
            </div>
            <div
                v-if="hotelAddress"
                class="mb-0">
                <div class="info-container__wrapper-icon d-flex justify-center">
                    <LocationIcon />
                </div>
                <span class="ml-2">
                    {{ hotelAddress | fullLocation }}
                </span>
            </div>
            <a
                v-if="hotelAddress"
                :href="directionsUrl"
                target="_blank"
                class="directions"
                ><strong>{{ $t('getDirections') }}</strong></a
            >
            <div
                v-if="!isPackageOffer && hotelRoomCancellationPolicies"
                class="mt-2 mb-3">
                <HotelCancellationPolicy
                    :cancellation-policies="hotelRoomCancellationPolicies"
                    :currency="currency"
                    :base-price="hotelRoomRate"
                    :accomodation-nights="hotelAccommodation.nights" />
            </div>
            <slot name="cta"></slot>
        </div>
    </div>
</template>

<script>
import { getLang as documentUtilsGetLang } from '@core/utils/documentUtils';
import StarRating from '@core/shared/components/misc/StarRating.vue';
import { getRouteBetweenTwoPoints, getRouteToPoint } from '@core/utils/locationMapUtils';
import offerConstants from '@core/utils/constants/offer';
import CalendarIcon from '@tenant/app/assets/icons/calendar.svg';
import LocationIcon from '@tenant/app/assets/icons/location.svg';
import { IMAGE_SIZE } from '@tenant/app/utils/constants/imageData';
import { getImageByWidth, getImageModel } from '@tenant/app/utils/imageModelUtils';
import GiftCardChip from '@tenant/app/components/chips/GiftCardChip';
import HotelCancellationPolicy from '@tenant/app/views/cart/TheCheckout/HotelCancellationPolicy';

export default {
    name: 'HotelReservationInfo',
    components: {
        CalendarIcon,
        LocationIcon,
        StarRating,
        HotelCancellationPolicy,
        GiftCardChip,
    },
    props: {
        currency: {
            type: String,
            required: true,
        },
        hotelImage: {
            type: [Object, null],
            default: null,
        },
        hotelAccommodation: {
            type: [Object, null],
            default: null,
        },
        hotelAddress: {
            type: [Object, null],
            default: null,
        },
        hotelName: {
            type: [String, null],
            default: null,
        },
        hotelRating: {
            type: [Object, null],
            default: null,
        },
        hotelSupplierConfirmationNumber: {
            type: [String, null],
            default: null,
        },
        hotelRoomCancellationPolicies: {
            type: [Array, null],
            default: null,
        },
        hotelRoomRate: {
            type: [Number, null],
            default: null,
        },
        hotelPackageOffer: {
            type: Object,
            default: null,
        },
        eventLocation: {
            type: Object,
            default: null,
        },
        isPackageOffer: {
            type: Boolean,
            default: false,
        },
        hasHotelReservationOnly: {
            type: Boolean,
            required: true,
        },
        giftCard: {
            type: Object,
            default: () => {},
        },
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.cart.theOrderConfirmation',
    },
    computed: {
        period() {
            let period = this.$options.filters.datePeriod(
                this.hotelAccommodation.startDateTime,
                this.hotelAccommodation.endDateTime,
                {
                    full: 'eee • MMM d, yyyy',
                    time: 'p',
                    period: 'eee • MMM d',
                    periodFull: 'eee • MMM d, yyyy',
                },
                documentUtilsGetLang(),
                false
            );

            return `${period} (${this.$t('_common:terms.nightStayWithCount', { count: this.hotelAccommodation.nights })})`;
        },
        hotelImageSrc() {
            if (
                this.hotelPackageOffer &&
                this.hotelPackageOffer.type === offerConstants.TYPES.PRE_BUNDLE_PACKAGE
            ) {
                return getImageByWidth(
                    IMAGE_SIZE.LARGE,
                    getImageModel(this.hotelPackageOffer.imageUrl)
                );
            }
            return getImageByWidth(IMAGE_SIZE.LARGE, this.hotelImage);
        },
        directionsUrl() {
            const hotelLocation = {
                street: this.hotelAddress.address,
                cityName: this.hotelAddress.cityName,
                stateCode: this.hotelAddress.stateCode,
                countryCode: this.hotelAddress.countryCode,
            };

            if (!this.eventLocation) {
                return getRouteToPoint(hotelLocation);
            }

            const directionsUrl = getRouteBetweenTwoPoints(this.eventLocation, hotelLocation);

            return directionsUrl;
        },
        showGiftCard() {
            return this.giftCard?.amount;
        },
        hotelConfirmationNumber() {
            return this.$t('hotelConfirmationNumber', {
                number: this.hotelSupplierConfirmationNumber,
            });
        },
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

.gst-hotel-reservation-info {
    width: 100%;
    gap: 11px;

    @include desktop-only {
        gap: them-spacing(4);
    }

    .image-container {
        img {
            display: block;
            width: 100%;
            min-height: 200px;
            object-fit: cover;
        }
    }

    .info-container {
        width: 100%;

        div {
            display: flex;
            line-height: line-height('m');
            color: theme-color('quaternary');
            font-size: font-size('xxs');
            align-items: center;

            .info-container__wrapper-icon {
                width: 15px;

                svg {
                    width: 15px;
                }
            }
        }

        .info-container__confirmation-number {
            font-size: font-size('xxs');
        }

        .event-title-container {
            margin-bottom: theme-spacing(3);

            .event-title {
                line-height: line-height('xxl');
                font-size: font-size('m');
                font-weight: font-weight('bold');
            }

            .event-rating {
                position: relative;
                bottom: 1px;
                margin-left: theme-spacing(2);
            }
        }

        button {
            width: 220px;
            padding: theme-spacing(2) theme-spacing(9);
            margin-bottom: theme-spacing(2);
            background: theme-color('primary');
            color: white;
            font-weight: font-weight('large');
            border-radius: 4px;
        }

        .directions {
            line-height: line-height('m');
            margin-top: theme-spacing(1);
            color: theme-color('primary');
            font-size: font-size('xxs');
            margin-left: theme-spacing(6);
            cursor: pointer;
        }

        .location-img,
        .tickets-img {
            position: relative;
            left: 2px;
        }

        .check-icon {
            .gst-svg-icon {
                fill: theme-color('success');
            }
        }

        .cancellation-policy-label {
            color: theme-color('success');
        }
    }

    .freeCancellation {
        color: theme-color('success');
    }
}

.gst-hotel-reservation-info__tooltip.v-tooltip__content {
    max-width: 350px !important;
}

.gst-hotel-reservation-info.gst-hotel-reservation-info--display-v2 {
    display: flex;
    width: 80%;
    margin-top: theme-spacing(6);
    align-items: center;
    justify-content: center;

    .image-container,
    .info-container {
        flex: 1;
    }

    .info-container {
        display: flex;
        flex-direction: column;
        margin-left: theme-spacing(4);
    }

    @include mobile-only {
        width: 90% !important;
        flex-direction: column;

        .info-container {
            width: 100% !important;
        }
    }
}
</style>
